<template>
  <SourceFiltersSelector
    v-model="selected"
    :filter="loadMediaReleases"
    :placeholder="placeholder"
    @item="selectItem"
    @query="query = $event"
  >
    <template #option="{ option }">
      <div
        class="tw:flex tw:justify-between"
        :class="{ 'tw:opacity-50': !option.recipientCount }"
      >
        <div>
          {{ option.name }}
        </div>
        <div>
          {{ option.recipientCount }}
        </div>
      </div>
    </template>
  </SourceFiltersSelector>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import SourceFiltersSelector, {
  type SourceFiltersSelectorFilterResponse,
} from "shared/components/sources/SourceFiltersSelector.vue";
import { MediaRelease } from "shared/resources";
import { type Nullable } from "shared/types/utility";

interface MediaReleaseResource extends MediaRelease {
  id: string;
}

type MediaReleaseResponse =
  SourceFiltersSelectorFilterResponse<MediaReleaseResource>;

interface AISuggestedMediaReleaseSelectorProps {
  type?: "media_release" | "pitch";
}

const { type = "media_release" } =
  defineProps<AISuggestedMediaReleaseSelectorProps>();

const mediaRelease = defineModel<Nullable<MediaRelease>>("mediaRelease", {
  default: null,
});

const query = ref<string>("");
const selected = ref<string[]>([]);

const placeholder = computed<string>(() =>
  mediaRelease.value ? mediaRelease.value.name : getLocaleText("global.name")
);

async function loadMediaReleases(index: number): Promise<MediaReleaseResponse> {
  try {
    const filters = {
      name: { match: query.value },
      media_release_type: { eq: type },
      status: { eq: "draft" },
    };

    const response = await MediaRelease.where(filters)
      .selectExtra(["recipientCount"])
      .page(index + 1)
      .per(50)
      .order({
        id: "desc",
      })
      .stats({
        total: "count",
      })
      .all();

    const pages = Math.ceil(response.meta.stats.total.count / 50);

    return {
      items: response.data,
      pages,
    };
  } catch (error) {
    const text =
      type === "media_release"
        ? getLocaleText(
            "ai_suggested_media_release_selector.load_media_releases_error"
          )
        : getLocaleText(
            "ai_suggested_media_release_selector.load_pitches_error"
          );

    ErrorDialog(text, error);

    return {
      items: [],
      pages: 1,
    };
  }
}

function selectItem(item: MediaRelease): void {
  selected.value = [];
  mediaRelease.value = item;
}
</script>
